import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import { teamService, TeamDto, TeamOperatorDto } from '../../services/teamService';
import { apiErrorMessage } from '../../utils/apiUtils';

interface TeamsManagementProps {
  /**
   * Called after any change to the team list, so the roster's Team dropdown above this panel
   * reflects a team the moment it is created, renamed or deactivated. Without it the two
   * components hold independent copies of the same list and the roster silently goes stale.
   */
  onTeamsChanged?: () => void;
}

/**
 * Teams management panel for tenant configuration.
 *
 * Team membership is an access control: the operator list here becomes the `OperatorId IN (...)`
 * filter that decides which 911 recordings a scoped Supervisor may open. The UI is written to say
 * so — removals are confirmed, deactivation states its effect, and nothing that changes reach
 * happens on a single unguarded click.
 */
export const TeamsManagement: React.FC<TeamsManagementProps> = ({ onTeamsChanged }) => {
  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create team dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newTeamKey, setNewTeamKey] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit team dialog
  const [editTeam, setEditTeam] = useState<TeamDto | null>(null);
  const [editTeamKey, setEditTeamKey] = useState('');
  const [editName, setEditName] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [saving, setSaving] = useState(false);

  // Operators panel
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [operators, setOperators] = useState<TeamOperatorDto[]>([]);
  const [loadingOps, setLoadingOps] = useState(false);
  const [addOpId, setAddOpId] = useState('');
  const [addingOp, setAddingOp] = useState(false);
  const [removingOp, setRemovingOp] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  // Focus lands here after a chip is removed — the element it was on has just unmounted, and
  // without this the keyboard user is dropped back to the top of the document mid-task.
  const addOperatorRef = useRef<HTMLInputElement>(null);

  // Only the newest operator load may write to state. Removals fire concurrent requests, and
  // last-response-wins on an access-control list is a wrong answer shown confidently.
  const opsRequestRef = useRef(0);

  /** Refresh after a mutation. Not used for the initial load — see the effect below. */
  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);
      const data = await teamService.getAll();
      setTeams(data);
      setError(null);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to load teams'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load, inlined rather than calling loadTeams(). Two reasons, both deliberate: no state
  // is set synchronously in the effect body (`react-hooks/set-state-in-effect` — this is a new
  // file, so the existing house pattern is not a licence to add another), and an unmount
  // mid-request cannot write into a dead component. `loading` already starts true, so there is
  // nothing to set on the way in.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await teamService.getAll();
        if (!cancelled) {
          setTeams(data);
          setError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) setError(apiErrorMessage(err, 'Failed to load teams'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadOperators = useCallback(async (teamKey: string) => {
    const requestId = ++opsRequestRef.current;
    try {
      setLoadingOps(true);
      const data = await teamService.getOperators(teamKey);
      if (opsRequestRef.current === requestId) setOperators(data);
    } catch (err: unknown) {
      if (opsRequestRef.current === requestId) {
        setError(apiErrorMessage(err, 'Failed to load team operators'));
      }
    } finally {
      if (opsRequestRef.current === requestId) setLoadingOps(false);
    }
  }, []);

  const handleSelectTeam = (teamKey: string) => {
    if (selectedTeam === teamKey) {
      setSelectedTeam(null);
      setOperators([]);
    } else {
      setSelectedTeam(teamKey);
      loadOperators(teamKey);
    }
  };

  const notifyChanged = async () => {
    await loadTeams();
    onTeamsChanged?.();
  };

  const handleCreate = async () => {
    if (!newTeamKey.trim() || !newTeamName.trim()) return;
    try {
      setCreating(true);
      await teamService.create(newTeamKey.trim(), newTeamName.trim());
      setSuccess(`Team "${newTeamName.trim()}" created. It has no operators yet, so nobody assigned to it will see any calls until you add some.`);
      setCreateOpen(false);
      setNewTeamKey('');
      setNewTeamName('');
      await notifyChanged();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to create team'));
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (team: TeamDto) => {
    setEditTeam(team);
    // Copied into their own state so the dialog's title and fields do not empty out during the
    // close transition when editTeam is cleared.
    setEditTeamKey(team.teamKey);
    setEditName(team.name);
    setEditActive(team.isActive);
  };

  const handleSaveEdit = async () => {
    if (!editTeam) return;
    try {
      setSaving(true);
      await teamService.update(editTeam.teamKey, editName.trim(), editActive);
      setSuccess(
        editTeam.isActive && !editActive
          ? `Team "${editName.trim()}" deactivated. Supervisors assigned to it now see no calls until it is reactivated or they are reassigned.`
          : `Team "${editName.trim()}" updated`
      );
      setEditTeam(null);
      await notifyChanged();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to update team'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddOperator = async () => {
    if (!selectedTeam || !addOpId.trim()) return;
    try {
      setAddingOp(true);
      await teamService.addOperator(selectedTeam, addOpId.trim());
      setSuccess(`Operator "${addOpId.trim()}" added to team`);
      setAddOpId('');
      await loadOperators(selectedTeam);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to add operator'));
    } finally {
      setAddingOp(false);
    }
  };

  const handleRemoveOperator = async (operatorId: string) => {
    if (!selectedTeam) return;
    try {
      setRemovingOp(operatorId);
      await teamService.removeOperator(selectedTeam, operatorId);
      await loadOperators(selectedTeam);
      // Success only after the reload, so a failed refresh cannot stack a green "removed" over a
      // red error describing a list the user cannot see.
      setSuccess(`Operator "${operatorId}" removed from team`);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to remove operator'));
    } finally {
      setRemovingOp(null);
      // Closed BEFORE focus is moved. MUI's FocusTrap pulls focus back into an open dialog on
      // focusin, so focusing the field while this was still open was a silent no-op — and the
      // dialog's own restore target, the chip's delete button, has just unmounted.
      setConfirmRemove(null);
      // After paint: the button the removal came from is gone, and React has to commit the new
      // list before the field is focusable. Without this the keyboard user lands on <body>.
      requestAnimationFrame(() => addOperatorRef.current?.focus());
    }
  };

  const selectedTeamName = teams.find(t => t.teamKey === selectedTeam)?.name ?? selectedTeam;
  const isLastOperator = operators.length === 1;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label="Loading teams" />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)} role="status">
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Teams ({teams.length})</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<GroupAddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create Team
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No teams configured. Create a team to scope supervisors to a subset of operators.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              teams.map(team => (
                <TableRow
                  key={team.teamKey}
                  hover
                  selected={selectedTeam === team.teamKey}
                >
                  <TableCell>
                    <Chip label={team.teamKey} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{team.name}</TableCell>
                  <TableCell>
                    {/* Text carries the status; colour only reinforces it. */}
                    <Chip
                      label={team.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={team.isActive ? 'success' : 'default'}
                      variant={team.isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{new Date(team.createdDate).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    {/* A real button rather than a click handler on the row: membership is the
                        whole point of this panel, and a bare TableRow is unreachable by keyboard. */}
                    <Tooltip title="Manage operators">
                      <IconButton
                        size="small"
                        onClick={() => handleSelectTeam(team.teamKey)}
                        aria-label={`Manage operators in team ${team.name}`}
                        aria-expanded={selectedTeam === team.teamKey}
                        aria-controls={selectedTeam === team.teamKey ? 'team-operators-panel' : undefined}
                      >
                        <GroupIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit team">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(team)}
                        aria-label={`Edit team ${team.name}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Operators panel for selected team */}
      {selectedTeam && (
        <Paper id="team-operators-panel" variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Operators in "{selectedTeamName}"
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Supervisors assigned to this team see only these operators' calls, reports and search
            results. A team with no operators shows them nothing.
          </Typography>

          {/* The editor is NOT unmounted while operators reload. Swapping it for a spinner tore
              out whichever control the user was on — the add field after an add, the list after a
              removal — and dropped focus to <body> on every single mutation. The list marks itself
              busy instead, and only the list dims. */}
          <Box aria-busy={loadingOps} sx={{ opacity: loadingOps ? 0.6 : 1 }}>
            {loadingOps && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary" role="status">
                  Updating operators…
                </Typography>
              </Box>
            )}
            <>
              <Box
                component="ul"
                sx={{
                  display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2,
                  listStyle: 'none', p: 0, m: 0,
                }}
                aria-label={`Operators in team ${selectedTeamName}`}
              >
                {operators.length === 0 ? (
                  <Typography component="li" variant="body2" color="text.secondary">
                    No operators assigned to this team yet.
                  </Typography>
                ) : (
                  operators.map(op => (
                    <Box
                      component="li"
                      key={op.operatorId}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        border: 1, borderColor: 'divider', borderRadius: 4,
                        pl: 1.5, pr: 0.5, py: 0.25,
                      }}
                    >
                      <Typography variant="body2">{op.operatorId}</Typography>
                      {/* A real IconButton with its own name, not a Chip's delete affordance:
                          the Chip variant announces only the operator id and responds to
                          Backspace rather than Enter or Space. */}
                      <Tooltip title={`Remove ${op.operatorId} from team`}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setConfirmRemove(op.operatorId)}
                            disabled={removingOp === op.operatorId}
                            aria-label={`Remove ${op.operatorId} from team ${selectedTeamName}`}
                          >
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  ))
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  label="Operator ID"
                  inputRef={addOperatorRef}
                  helperText="Must match the operator identifier in audio filenames, e.g. JSMITH"
                  value={addOpId}
                  onChange={e => setAddOpId(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddOperator(); }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={handleAddOperator}
                  disabled={addingOp || !addOpId.trim()}
                  sx={{ mt: 0.5 }}
                >
                  Add
                </Button>
              </Box>
            </>
          </Box>
        </Paper>
      )}

      {/* Create Team Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="create-team-title"
      >
        <DialogTitle id="create-team-title">Create Team</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Team Key"
            helperText="Short identifier, e.g. alpha (cannot be changed after creation)"
            value={newTeamKey}
            onChange={e => setNewTeamKey(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Team Name"
            helperText="Shown on the roster, e.g. Alpha Squad"
            value={newTeamName}
            onChange={e => setNewTeamName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
        </DialogContent>
        <DialogActions>
          {/* Cleared on cancel — an abandoned attempt should not reappear half-filled next time.
              The Edit dialog deliberately keeps its values, because they are the team's own. */}
          <Button onClick={() => { setCreateOpen(false); setNewTeamKey(''); setNewTeamName(''); }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={creating || !newTeamKey.trim() || !newTeamName.trim()}
          >
            {creating ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog
        open={!!editTeam}
        onClose={() => setEditTeam(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="edit-team-title"
      >
        <DialogTitle id="edit-team-title">Edit Team: {editTeamKey}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Team Name"
            value={editName}
            onChange={e => setEditName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <FormControlLabel
            control={<Switch checked={editActive} onChange={e => setEditActive(e.target.checked)} />}
            label="Active"
            sx={{ mt: 1 }}
          />
          {!editActive && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              While inactive, supervisors assigned to this team see no calls, reports or search
              results at all. Reassign them first if they still need access.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTeam(null)}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={saving || !editName.trim()}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm operator removal — it changes who can read that operator's recordings. */}
      <Dialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="remove-operator-title"
      >
        <DialogTitle id="remove-operator-title">Remove operator from team?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{confirmRemove}</strong> will be removed from "{selectedTeamName}". Supervisors
            scoped to this team will no longer see this operator's calls, reports or search results.
          </DialogContentText>
          {isLastOperator && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This is the team's last operator. Once removed, supervisors assigned to this team see
              nothing until another operator is added.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)}>Cancel</Button>
          <Button
            onClick={() => confirmRemove && handleRemoveOperator(confirmRemove)}
            variant="contained"
            color="error"
            disabled={!!removingOp}
          >
            {removingOp ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
